const _ = require("lodash");
const moment = require("moment");
const StringUtil = require("../../../shared/StringUtil");
const ChapterSection = require("../../../shared/ChapterSection");
const GradeChapter = require("../../../shared/GradeChapter");
const colors = require("./ColorUtil").getCurrentScheme();
const TrainerCore = require("../../../shared/TrainerCore");
const OpsUser = require("../vault/OpsUser");
const {FLAGS} = require("./GlobalFlags");
const RoundSums = require("../../../shared/RoundSums");

const getStarBarSizes = function(chapter) {
	const total = chapter.total;
	const starBarSizes = _.map(chapter.byStars, x =>
		total === 0 ? 0 : x / total
	);
	return starBarSizes;
};

const getNumBlocksCompleted = function(chapters) {
	const regularBlocks = chapters.reduce((x, c) => x + _.sum(c.byStars), 0);
	return regularBlocks;
};

const getNumLabBlocksCompleted = function(types) {
	return types.reduce((x, t) => x + _.sum(_.values(t.celestialsEarned)), 0);
};

const getNumTotalLabBlocks = function(types) {
	const typesToCount = _.filter(
		types,
		t =>
			FLAGS.accessUnderConstructionLabTypes ||
			(t.releasedAt && !t.underConstruction)
	);
	return typesToCount.reduce((x, t) => x + t.total, 0);
};

const getNumTotalBlocksForGrade = function(gradeID) {
	const chapterIDs = GradeChapter.getChaptersForGrade(gradeID);
	return _.sum(chapterIDs.map(GradeChapter.getNumBlocksInChapterID));
};

const getChapterBlockStarsEarned = function(chapter) {
	return (
		_.sum(_.map(chapter.byStars, (x, i) => x * i)) +
		chapter.challengeBlock.stars +
		chapter.reviewBlock.stars +
		chapter.testBlock.stars
	);
};

const getInitialGrade = function(profileData) {
	let gradeNumber = profileData.activity.lastPlayedData.gradeNumber;
	// if no last played data, use last visited page
	if (!gradeNumber) {
		const data =
			profileData.activity.mostRecentPageData ||
			OpsUser.getDefaultMostRecentPageData();
		gradeNumber = data.gradeNumber;
	}
	const activeGrades = GradeChapter.getActiveGrades();
	return _.includes(activeGrades, gradeNumber) ? gradeNumber : activeGrades[0];
};

const getLastPlayedDate = function(activityData, allowsDefaulting) {
	const lastPlayedString = activityData.lastPlayed;

	let lastPlayed;

	if (lastPlayedString) {
		lastPlayed = moment(lastPlayedString); // TODO: don't rely on moment's ability to parse the string lastPlayed by default
	} else if (allowsDefaulting) {
		lastPlayed = moment();
	}

	return lastPlayed;
};

const getMinuteString = function(minutes) {
	return Math.ceil(minutes) + " min";
};

const getDurationString = function(inMinutes) {
	return StringUtil.toTimeString(inMinutes);
};

const getDurationShortString = function(inMinutes) {
	const hours = Math.floor(inMinutes / 60);
	const minutes = Math.ceil(inMinutes % 60);

	const hoursString = hours > 0 ? hours + "h " : "";

	return hoursString + minutes + "m";
};

const getDurationVeryShortString = function(inMinutes) {
	if (inMinutes === 0) return "";

	const hours = Math.floor(inMinutes / 60);
	const minutes = Math.ceil(inMinutes % 60);

	if (hours > 0) return hours + _.round(minutes / 60) + "h";

	return minutes + "m";
};

const getMinutesForTimeData = function(timeData) {
	return Math.ceil(_.sum(_.values(timeData)) / 60);
};

const getDurationShortStringForSeconds = function(inSeconds) {
	return getDurationShortString(Math.ceil(inSeconds / 60));
};

const getDurationShortStringForTimeData = function(timeData) {
	return getDurationShortString(getMinutesForTimeData(timeData));
};

const getSecondaryInfoForStudentData = function(studentData) {
	let secondaryInfoText;

	if (studentData.username) {
		secondaryInfoText = "Username: " + studentData.username;
	} else if (studentData.registrationCode) {
		if (studentData.displayName) {
			secondaryInfoText = "Registration Code: " + studentData.registrationCode;
		}
	}

	return secondaryInfoText;
};

const toMinutes = sec => Math.ceil(sec / 60);

const formatCalendarTime = (sec, style) => {
	const mins = toMinutes(sec);
	const ending = _.includes(["office", "school"], style) ? "m" : " min";
	if (style === "trainer" && sec < 60) {
		return "<1 min";
	}
	return mins + ending;
};

const getDayStats = function(day, monthData) {
	if (!monthData) {
		return null;
	}

	const dayData = monthData[day];
	let dayStats;

	if (dayData) {
		const timeData = dayData.timeData;
		dayStats = _.pick(dayData, [
			"stars",
			"xp",
			"bucks",
			"trophies",
			"problemsCorrect",
			"totalProblems",
			"libraryData",
			"theaterData",
		]);

		const timeTermsRaw = [
			timeData.trainer || 0,
			timeData.library || 0,
			timeData.theater || 0,
			timeData.lab || 0,
			timeData.beastbuilder || 0,
			timeData.leaderboards || 0,
		];
		const timeTotalRaw = _.sum(timeTermsRaw);
		const rounded = RoundSums.roundToMatchSum(timeTotalRaw, timeTermsRaw, 60);

		dayStats.timePlayed = {
			total: toMinutes(rounded.sum),
			played: toMinutes(rounded.terms[0]),
			read: toMinutes(rounded.terms[1]),
			watched: toMinutes(rounded.terms[2]),
			puzzled: toMinutes(rounded.terms[3]),
			// "other" and "beastbuilder" are the same, but we abandoned
			// using other here because we need to separately keep track of
			// beastbuilder and leaderboards minutes. We include "other"
			// for now so as not to cause errors for people on old versions
			// of BA that still use "other" on the front end.
			other: toMinutes(rounded.terms[4]),
			beastbuilder: toMinutes(rounded.terms[4]),
			leaderboards: toMinutes(rounded.terms[5]),
		};
	} else {
		dayStats = {
			stars: 0,
			xp: 0,
			bucks: 0,
			trophies: 0,
			problemsCorrect: 0,
			totalProblems: 0,
			timePlayed: {
				total: 0,
				played: 0,
				read: 0,
				watched: 0,
				puzzled: 0,
				other: 0,
				beastbuilder: 0,
				leaderboards: 0,
			},
			libraryData: {},
		};
	}

	return dayStats;
};

// Returns dayActivity.data.results with times rounded to the minute using
// RoundSums, where the sum comes from dayStats.
const getDayActivityPlayed = function(dayActivity, dayStats) {
	const results = _.get(dayActivity, "data.results", []);
	if (!results || !results.length) return null;

	const timesRaw = results.map(r => r.timeSpent || 0);
	const totalRaw = _.get(dayStats, "timePlayed.played") * 60 || 0;
	const rounded = RoundSums.roundToMatchSum(totalRaw, timesRaw, 60);
	return results.map((r, i) => {
		return _.extend({}, r, {timeSpent: rounded.terms[i]});
	});
};

// Returns monthActivity.data[activeDay].libraryData with times rounded to the
// minute using RoundSums, where the sum comes from dayStats.
const getDayActivityRead = function(monthActivity, activeDay, dayStats) {
	const timesBySection = _.get(monthActivity, [
		"data",
		activeDay,
		"libraryData",
	]);
	if (!timesBySection || _.isEmpty(timesBySection)) return null;

	const sections = _.keys(timesBySection);

	const timesRaw = sections.map(s => timesBySection[s] || 0);
	const totalRaw = _.get(dayStats, "timePlayed.read") * 60 || 0;
	const rounded = RoundSums.roundToMatchSum(totalRaw, timesRaw, 60);

	const ret = _.clone(timesBySection);
	sections.forEach((s, i) => (ret[s] = rounded.terms[i]));
	return ret;
};

// Returns dayActivity.data.videos with times rounded to the minute using
// RoundSums, where the sum comes from dayStats.
const getDayActivityWatched = function(dayActivity, dayStats) {
	const videos = _.get(dayActivity, "data.videos", []);
	if (!videos || !videos.length) return null;

	const timesRaw = videos.map(r => r.timeInSet || 0);
	const totalRaw = _.get(dayStats, "timePlayed.watched") * 60 || 0;
	const rounded = RoundSums.roundToMatchSum(totalRaw, timesRaw, 60);
	return videos.map((r, i) => {
		return _.extend({}, r, {timeInSet: rounded.terms[i]});
	});
};

// Returns dayActivity.data.labResults with times rounded to the minute using
// RoundSums, where the sum comes from dayStats.
const getDayActivityPuzzled = function(dayActivity, dayStats) {
	const results = _.get(dayActivity, "data.labResults", []);
	if (!results || !results.length) return null;

	const timesRaw = results.map(r => r.timeSpent || 0);
	const totalRaw = _.get(dayStats, "timePlayed.puzzled") * 60 || 0;
	const rounded = RoundSums.roundToMatchSum(totalRaw, timesRaw, 60);
	return results.map((r, i) => {
		return _.extend({}, r, {timeSpent: rounded.terms[i]});
	});
};

// Take daily read activity and sort by grade and chapter
const sortSectionsByChapter = function(sectionData) {
	const withGradeChapter = _.map(sectionData, (time, sectionID) => {
		const gradeChapter = ChapterSection.getContextFromSectionID(
			parseInt(sectionID)
		);
		return gradeChapter ? _.extend(gradeChapter, {time}) : null;
	});
	// Filter out sectionIDs that can't be found.
	const orderWith = withGradeChapter.filter(d => d);

	return _.orderBy(
		orderWith,
		["gradeNumber", "chapterIndex", "sectionIndex"],
		["asc", "asc", "asc"]
	);
};

const getTrialsDataForGraph = function(problem) {
	let backgroundColor = colors.lightGrayFill;
	if (!problem) return {backgroundColor, data: []};
	const {trials, outcome, saved} = problem;
	if (!_.get(trials, [0]) && !outcome) {
		if (saved) {
			backgroundColor = colors.unfinalizedFill;
		}
		return {backgroundColor, data: []};
	}
	// Show at least one empty trial if problem is not finished.
	// Also don't show too much red, at most 3/4 red.
	const total = outcome
		? Math.min(4, trials.length)
		: Math.max(trials.length, 2);
	const isCorrect = outcome === "correct";
	const isIncorrect = outcome === "incorrect" || outcome === "giveup";
	const isSkipped = outcome === "skipped";
	const data = [];
	// Since we're drawing pie graph clockwise,
	// we need to draw the green and gray before we draw the red.
	if (isCorrect) {
		backgroundColor = colors.correctFill;
		data.push({
			color: colors.correctFill,
			percent: _.round(1 / total, 2),
		});
	} else if (isIncorrect) {
		backgroundColor = colors.incorrectFill;
	} else if (isSkipped) {
		backgroundColor = colors.skippedFill;
	} else {
		data.push({
			color: colors.lightGrayFill,
			percent: _.round(1 / total, 2),
		});
	}
	data.push({
		color: colors.incorrectFill,
		percent: total ? _.round(1 - 1 / total, 2) : 0,
	});
	return {data, backgroundColor};
};

module.exports = {
	getStarBarSizes,
	getNumTotalLabBlocks,
	getNumTotalBlocksForGrade,
	getNumBlocksCompleted,
	getNumLabBlocksCompleted,
	getChapterBlockStarsEarned,
	getInitialGrade,
	getLastPlayedDate,
	getMinuteString,
	getDurationString,
	getDurationShortString,
	getDurationVeryShortString,
	getDurationShortStringForSeconds,
	getDurationShortStringForTimeData,
	getMinutesForTimeData,
	getSecondaryInfoForStudentData,
	getDayStats,
	getDayActivityPlayed,
	getDayActivityRead,
	getDayActivityWatched,
	getDayActivityPuzzled,
	sortSectionsByChapter,
	sortSectionsIntoChapters: TrainerCore.sortSectionsIntoChapters,
	toMinutes,
	getTrialsDataForGraph,
	formatCalendarTime,
};
